import { defaultPropertyDefinitions } from "@buildwithflux/constants";
import { naturalSortByProperty } from "@buildwithflux/shared";
import { groupBy, pick, pickBy, uniq } from "lodash";
import { ElementHelper, shouldHaveFootprint } from "../../../elements";
import { PcbNodeHelper } from "../../../nodes";
import { createSubjectExpressionContext, stringifyPropertyValue } from "../../../pcb";
import { getIsExcludedFromBOM } from "../../../property";
import { getAbsoluteDocumentUrlFromUid } from "../../../url";
import { BaseExporter } from "../BaseExporter";
import { AdvancedCircuitsFormat, AllPCBFormat, ElecrowFormat, EurocircuitsFormat, FluxFormat, JLCPCBBOMFormat, PCBWayFormat, SeeedFormat, } from "./formats";
const REQUIRED_DATAROW_FIELDS = [
    "Flux.ai Element ID",
    "Quantity",
    "Designator",
    "Position X",
    "Position Y",
    "Rotation",
    "Flipped",
    "Part Name",
    "Part ID",
    "Part Version",
    "Number of Terminals",
    "Terminals",
];
const exportFormats = [
    AdvancedCircuitsFormat,
    AllPCBFormat,
    EurocircuitsFormat,
    JLCPCBBOMFormat,
    PCBWayFormat,
    FluxFormat,
    SeeedFormat,
    ElecrowFormat,
];
function toLineItem(elementProperties) {
    return (row) => {
        const valueProp = getValueProperty(row);
        return {
            partUid: row["Part ID"],
            partName: row["Part Name"],
            partDocumentUrl: row["Flux.ai url"] ?? "",
            mpn: row["Manufacturer Part Number"] ?? "",
            quantity: row.Quantity,
            designators: row.Designator.split(","),
            specification: buildSpecification(row),
            manufacturer: row["Manufacturer Name"] ?? row.Manufacturer ?? "",
            mountType: row["Mount Type"] ?? "",
            value: valueProp ? row[valueProp] : "",
            package: row["Package or Case Code"] ?? row["Package"],
            notes: row.Notes ?? "",
            description: row.Description ?? `${row["Part Type"]} ${row[getValueProperty(row) ?? "Part Name"]}}`,
            elementUids: (row["Flux.ai Element ID"] ?? "").split(","),
            elementProperties: (row["Flux.ai Element ID"] ?? "")
                .split(",")
                .reduce((acc, elementUid) => {
                acc[elementUid] = elementProperties[elementUid] ?? {};
                return acc;
            }, {}),
            dpn: row["Distributor Part Number"] ?? row["DPN"] ?? "",
            distributorName: row["Preferred Distributors"] ?? "",
            schematicPositions: {
                [row["Flux.ai Element ID"]]: {
                    x: parseFloat(row["Position X"]),
                    y: parseFloat(row["Position Y"]),
                    rotation: row.Rotation,
                    flipped: row.Flipped,
                },
            },
        };
    };
}
/**
 * Basic function to find the correct value property based on the part type. The matching is dumb
 * but getting it wrong is low impact - elements with missing or weird sets of properties just won't
 * be grouped together.
 *
 * TODO: revisit this once we can model these relationships in property definitions.
 *
 * TODO: I think the functionality of this function is already covered more generally by
 * Element.getDefaultPartProperty() (which admittedly could be more straightforward itself)
 */
function getValueProperty(row) {
    if (row["Part Type"]?.match(/Capacitor/)) {
        return "Capacitance";
    }
    else if (row["Part Type"]?.match(/Resistor/)) {
        return "Resistance";
    }
    else if (row["Part Type"]?.match(/Inductor/)) {
        return "Inductance";
    }
    else {
        return undefined;
    }
}
function buildSpecification(row) {
    const valueProp = getValueProperty(row);
    const attrs = [row["Part Type"] ?? ""];
    if (valueProp && row[valueProp]) {
        attrs.push(row[valueProp]);
    }
    const pkg = row["Package or Case Code"] || row["Package"];
    if (pkg) {
        attrs.push(pkg);
    }
    return attrs;
}
function buildGroupString(row) {
    if (row["Manufacturer Part Number"]) {
        return row["Manufacturer Part Number"].trim();
    }
    const valueProp = getValueProperty(row);
    if (valueProp && row[valueProp]) {
        return `${row["Part Type"].trim()}-${(row[valueProp] ?? "").trim()}`;
    }
    else if (row["Part ID"]) {
        return row["Part ID"];
    }
    else {
        return row["Flux.ai Element ID"];
    }
}
function sortedAppend(base, toAppend) {
    if (base) {
        return uniq(base.split(",").concat(toAppend.split(",")).sort()).join(",");
    }
    else {
        return toAppend;
    }
}
/**
 * BillOfMaterialsExporter - Pure business logic for BOM generation.
 * This class contains the core data transformation logic for generating BOM data.
 * Browser-specific download functionality should be handled by frontend adapters.
 */
export class BillOfMaterialsExporter extends BaseExporter {
    static hasThingsToExport(elements) {
        return Object.keys(BillOfMaterialsExporter.getValidElementNodes(elements)).length > 0;
    }
    static getValidElementNodes(elements, parentElementUid) {
        const bomElements = {};
        for (const [elementUid, element] of Object.entries(elements)) {
            if (!element)
                continue;
            const fullElementUid = parentElementUid
                ? PcbNodeHelper.constructNodeUidForSubLayout(parentElementUid, elementUid)
                : elementUid;
            // Determine if we should add this element to the BOM and/or recurse into its sub-elements
            const isValidForBom = ElementHelper.isValidBomElement(element);
            // Check for explicit exclusion reasons that should prevent recursion
            const isAlwaysExcluded = ElementHelper.isTerminalElement(element) ||
                ElementHelper.isGroundElement(element) ||
                ElementHelper.isBranchPointElement(element);
            const isExplicitlyExcluded = element.properties && getIsExcludedFromBOM(element.properties);
            // Elements without footprints (modules/sublayouts) are containers that may have valid sub-elements
            // BUT only if they're not explicitly excluded or always-excluded
            const isContainerElement = !shouldHaveFootprint(element) && !isAlwaysExcluded && !isExplicitlyExcluded;
            if (isValidForBom) {
                bomElements[fullElementUid] = element;
            }
            // Recurse into sub-elements for valid elements OR container elements (modules/sublayouts)
            // Don't recurse for always-excluded elements (terminals/grounds/branch-points) or explicitly excluded elements
            if (isValidForBom || isContainerElement) {
                const subElements = BillOfMaterialsExporter.getValidElementNodes(element.part_version_data_cache?.extracted_element_cache ?? {}, fullElementUid);
                Object.assign(bomElements, subElements);
            }
        }
        return bomElements;
    }
    /**
     * Creates the BOM data in various manufacturer formats.
     * This is the pure business logic that can be used in both frontend and backend.
     */
    createBomData() {
        const dataRows = [];
        const elements = BillOfMaterialsExporter.getValidElementNodes(this.documentData.elements);
        const elementProperties = {};
        for (const [elementUid, element] of Object.entries(elements)) {
            const properties = this.getProperties(element, elementUid);
            elementProperties[elementUid] = properties;
            const isExtractedElement = ElementHelper.isExtractedElement(element);
            const dataRow = {
                "Flux.ai Element ID": elementUid,
                Quantity: 1,
                Designator: element.label || "",
                "Position X": "0",
                "Position Y": "0",
                Rotation: 0,
                Flipped: false,
                ...properties,
                "Part Name": element.part_version_data_cache?.name ?? "",
                Description: element.part_version_data_cache?.description ?? "",
                "Part ID": element.part_version_data_cache?.part_uid ?? "",
                "Part Version": element.part_version_data_cache?.version ?? "",
                "Number of Terminals": Object.keys(element.part_version_data_cache?.terminals ?? {}).length.toString() ?? "",
                "Flux.ai url": this.getAbsoluteUrl(element),
                Terminals: isExtractedElement ? "" : this.getTerminals(element),
            };
            dataRows.push(dataRow);
        }
        const groups = groupBy(this.removeUnusedColumns(dataRows), buildGroupString);
        const groupedDataRows = Object.values(groups)
            .map((elementRows) => {
            return elementRows.reduce((row, mergedRow) => {
                mergedRow.Quantity += row.Quantity;
                mergedRow["Flux.ai Element ID"] = sortedAppend(mergedRow["Flux.ai Element ID"], row["Flux.ai Element ID"]);
                mergedRow["Flux.ai url"] = row["Flux.ai url"];
                mergedRow.Designator = sortedAppend(mergedRow.Designator, row.Designator);
                return mergedRow;
            });
        })
            .sort(naturalSortByProperty("Designator"));
        const lineItems = groupedDataRows.map(toLineItem(elementProperties));
        return exportFormats.map((format) => new format(lineItems));
    }
    /**
     * Creates a filename for the BOM export.
     */
    createBomFileName(extension, versionShortCode) {
        const exportName = `BOM - V${versionShortCode ?? "0"}`;
        return this.createFileName(extension, exportName);
    }
    getAbsoluteUrl(element) {
        const documentUid = ElementHelper.isExtractedElement(element)
            ? element.document_import_uid
            : element.part_version_data_cache.document_import_uid;
        if (documentUid) {
            return getAbsoluteDocumentUrlFromUid(documentUid);
        }
        else {
            return undefined;
        }
    }
    removeUnusedColumns(data) {
        const keysToStay = uniq(data.flatMap((row) => Object.keys(pickBy(row, (v) => !!v))));
        keysToStay.push(...REQUIRED_DATAROW_FIELDS);
        return data.map((row) => pick(row, keysToStay));
    }
    getTerminals(element) {
        const propertiesStringArray = [];
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        Object.values(element.part_version_data_cache.terminals ?? {}).forEach((terminal) => {
            let terminalString = `${terminal.uid}:${terminal.name || ""}`;
            if (terminal.type) {
                terminalString = terminalString + `(${terminal.type})`;
            }
            propertiesStringArray.push(terminalString);
        });
        return propertiesStringArray.join(" ");
    }
    getProperties(element, elementUid) {
        const properties = {};
        const elementProperties = Object.values(element.properties ?? {});
        const defaultProperties = Object.values(defaultPropertyDefinitions);
        const bomDefaultProperties = defaultProperties.filter((prop) => prop.exclude_from_bom !== true);
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        bomDefaultProperties.forEach((defaultProp) => {
            const property = elementProperties.find((prop) => prop.name === defaultProp.label);
            const linkedPcbLayoutElementNode = this.documentData.pcbLayoutNodes[elementUid];
            const context = createSubjectExpressionContext(element, this.documentData, linkedPcbLayoutElementNode);
            if (property && context) {
                properties[defaultProp.label] = stringifyPropertyValue({ unit: defaultProp.unit, ...property }, context);
            }
            else {
                properties[defaultProp.label] = "";
            }
        });
        const customProperties = elementProperties.filter((prop) => {
            const isDefaultProperty = bomDefaultProperties.find((defaultProp) => defaultProp.label === prop.name);
            return !isDefaultProperty;
        });
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        customProperties.forEach((customProp) => {
            if (customProp.name) {
                properties[customProp.name] = customProp.value;
            }
        });
        return properties;
    }
}
//# sourceMappingURL=BillOfMaterialsExporter.js.map